import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { sorted } from "helpers";
import { TFunction } from "i18next";
import { CrewOverrideList } from "src/store/api/generatedApi";

const COLUMN_COUNT = 6;

const LineList = ({ lines }: { lines: string[] }) =>
  lines.length === 0 ? "—" : lines.map((line) => <div key={line}>{line}</div>);

export const OverrideListTable = ({
  overrideList,
  t,
}: {
  overrideList: CrewOverrideList[];
  t: TFunction;
}) => {
  const theme = useTheme();
  const hairline = `1px solid ${theme.palette.grey[100]}`;
  const headers = [
    t("overrideHeatStart"),
    t("heats"),
    t("overrideReason"),
    t("deltaMaterialChanges"),
    t("comment"),
    t("addedBy"),
  ];
  const crewsWithOverrides = sorted(
    overrideList.filter((crew) => crew.overrides.length > 0),
    (crew) => crew.crew_id ?? t("noCrewOnShift")
  );
  if (crewsWithOverrides.length === 0) {
    return (
      <Typography sx={{ mt: 2, textAlign: "center", color: "text.secondary" }}>
        {t("overrideListEmpty")}
      </Typography>
    );
  }
  return (
    <Stack spacing={3} sx={{ mt: 2 }}>
      {crewsWithOverrides.map((crewOverrides) => (
        <Table
          key={crewOverrides.crew_id ?? ""}
          sx={{
            borderCollapse: "collapse",
            border: hairline,
            width: "100%",
            "& td, & th": {
              padding: "7px 10px",
              textAlign: "left",
              verticalAlign: "top",
              borderBottom: hairline,
              overflowWrap: "break-word",
            },
            "& td.time": { whiteSpace: "nowrap" },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                colSpan={COLUMN_COUNT}
                sx={{
                  ...theme.typography.body2,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  bgcolor: "grey.50",
                }}
              >
                {`${t("overrides")} — ${crewOverrides.crew_id === null ? t("noCrewOnShift") : `${t("crew")} ${crewOverrides.crew_id}`}`}
              </TableCell>
            </TableRow>
            <TableRow>
              {headers.map((header) => (
                <TableCell
                  key={header}
                  sx={{ ...theme.typography.body2, color: "text.secondary" }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {crewOverrides.overrides.map((override) => (
              <TableRow key={override.override_id}>
                <TableCell className="time">
                  {override.first_heat_start_time}
                </TableCell>
                <TableCell>
                  <LineList lines={override.heats} />
                </TableCell>
                <TableCell>{override.reason ?? "—"}</TableCell>
                <TableCell>
                  <LineList
                    lines={override.charge_changes.map(
                      (charge) =>
                        `${t("deltaChargeNumber", { charge: charge.basket_number })}: ${charge.material_changes.join(", ")}`
                    )}
                  />
                </TableCell>
                <TableCell>{override.comment ?? "—"}</TableCell>
                <TableCell>{override.created_by}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ))}
    </Stack>
  );
};
